import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  FormLabel,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  TextField,
  InputAdornment,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { format, parse } from 'date-fns';

const DATETIME_LOCAL = "yyyy-MM-dd'T'HH:mm";

function parseLocal(value: string | null): Date | undefined {
  if (!value) return undefined;
  const date = parse(value, DATETIME_LOCAL, new Date());
  return Number.isNaN(date.getTime()) ? undefined : date;
}
import { DEFAULT_WORK_DURATION_STEP_MINUTES, formatDuration, sumHours } from '@/utils/duration';
import { DurationHoursInput } from './DurationHoursInput';
import { AvailabilityTimePicker } from './AvailabilityTimePicker';
import {
  DEFAULT_PICKUP_BUFFER_MINUTES,
  hasKnownDuration,
  pickupFitsWorkWindow,
} from './pickupEstimate';
import type { SelectedServiceData } from './ServiceStep';
import type { SchedulingType, WorkshopOpeningHours } from '@/types';
import { openTimeForDate } from '@/utils/openingHours';
import { latestDropoffAt, suggestedDropoffAt } from './dropoffDefault';
import { useExactTimeOutsideShiftWarning } from '@/features/calendar/outsideShift';
import { guideAnchor } from '@/lib/guides/engine';

interface SchedulingStepProps {
  scheduledAtStr: string;
  setScheduledAtStr: (v: string) => void;
  vehicleDropoffAtStr?: string;
  setVehicleDropoffAtStr?: (v: string) => void;
  pickupAtStr: string;
  setPickupAtStr: (v: string) => void;
  estimatedDurationHours?: number;
  /**
   * Calendar-reserved duration setter (#2336). When omitted the field is
   * still shown (including 0) but read-only. Ignored once the order has a
   * service: that total is the derived sum and is not sent (#4123).
   */
  setEstimatedDurationHours?: (v: number) => void;
  /**
   * Replaces the overall duration field. Quote-accept with services uses
   * this for per-service planned hours and still passes the summed hours
   * as `estimatedDurationHours` for availability (#2667).
   */
  durationOverride?: ReactNode;
  /** Workshop step for the duration slider and arrows; 30 when unset (#3091). */
  durationStepMinutes?: number;
  mechanicIds?: string[];
  selectedServices: SelectedServiceData[];
  customerWaits: boolean;
  setCustomerWaits: (v: boolean) => void;
  allowMinorMaintenance: boolean;
  setAllowMinorMaintenance: (v: boolean) => void;
  preapprovedLimitInput: string;
  setPreapprovedLimitInput: (v: string) => void;
  jointInspectionRequested: boolean;
  setJointInspectionRequested: (v: boolean) => void;
  setIsDirty: (v: boolean) => void;
  /**
   * Exact-time vs during-the-day selection (#1138). Required: there is no
   * defensible hardcoded default any more — the initial value is the
   * workshop's `defaultSchedulingType` setting, which only the caller can
   * read (#1931). A caller that omits `setSchedulingType` still gets the
   * behavior for the type it passes, just without the toggle.
   */
  schedulingType: SchedulingType;
  setSchedulingType?: (v: SchedulingType) => void;
  /**
   * True once the user has picked a type themselves. "Customer waits" then
   * does not override it (#2115) — it is guidance, not a lock.
   */
  schedulingTypeTouched?: boolean;
  /** Workshop default restored when "Customer waits" is cleared (#2115). */
  defaultSchedulingType?: SchedulingType;
  /** Saved workshop opening hours; the exact-time default falls back to 08:00 without them (#4095). */
  businessHours?: WorkshopOpeningHours;
  /** Marks the type as a manual choice so customer-waits will not override it. */
  onSchedulingTypeUserChange?: () => void;
  /** Workshop pickup buffer in minutes; 60 when the workshop has never set it (#2117). */
  pickupBufferMinutes?: number;
  /**
   * Hide the in-step outside-shift alert when the parent shows the same
   * warning next to confirm (#2796).
   */
  hideOutsideShiftWarning?: boolean;
}

export function SchedulingStep({
  scheduledAtStr,
  setScheduledAtStr,
  vehicleDropoffAtStr = '',
  setVehicleDropoffAtStr,
  pickupAtStr,
  setPickupAtStr,
  estimatedDurationHours,
  setEstimatedDurationHours,
  durationOverride,
  durationStepMinutes = DEFAULT_WORK_DURATION_STEP_MINUTES,
  mechanicIds,
  selectedServices,
  customerWaits,
  setCustomerWaits,
  allowMinorMaintenance,
  setAllowMinorMaintenance,
  preapprovedLimitInput,
  setPreapprovedLimitInput,
  jointInspectionRequested,
  setJointInspectionRequested,
  setIsDirty,
  schedulingType,
  setSchedulingType,
  schedulingTypeTouched = false,
  defaultSchedulingType = 'floating',
  businessHours,
  onSchedulingTypeUserChange,
  pickupBufferMinutes = DEFAULT_PICKUP_BUFFER_MINUTES,
  hideOutsideShiftWarning = false,
}: SchedulingStepProps) {
  const { t } = useTranslation('workorders');

  // With services the server derives the reserved time from the rows. Show
  // that sum, including on the calendar, and do not offer an edit that
  // create would drop (#4123, #2785). No services: the caller's hours.
  const durationFromServices = selectedServices.length > 0;
  const durationHours = durationFromServices
    ? sumHours(selectedServices.map((service) => service.estimatedDurationHours))
    : (estimatedDurationHours ?? 0);
  const durationKnown = hasKnownDuration(durationHours);

  // Selected date from the picker (YYYY-MM-DD portion)
  const selectedDate = scheduledAtStr ? scheduledAtStr.split('T')[0] ?? '' : '';

  // Unique service IDs of the selected services — the availability endpoints
  // use them for skill-aware qualified capacity (epic #1140). With no skill
  // requirements every active mechanic qualifies, so passing them is always
  // safe.
  const serviceIds = useMemo(
    () => Array.from(new Set(selectedServices.map((s) => s.serviceId).filter(Boolean))),
    [selectedServices],
  );

  // Switching type keeps the chosen date: floating drops the time to 00:00,
  // exact falls back to the day's opening time the slot grid then refines
  // (#1142, #4095).
  const applySchedulingType = (next: SchedulingType) => {
    if (!setSchedulingType || next === schedulingType) return;
    setSchedulingType(next);
    if (selectedDate) {
      setScheduledAtStr(
        next === 'floating'
          ? `${selectedDate}T00:00`
          : `${selectedDate}T${openTimeForDate(selectedDate, businessHours)}`,
      );
    }
    setIsDirty(true);
  };

  const handleSchedulingTypeChange = (next: SchedulingType) => {
    applySchedulingType(next);
    onSchedulingTypeUserChange?.();
  };

  const handleCustomerWaitsChange = (checked: boolean) => {
    setCustomerWaits(checked);
    setIsDirty(true);
    if (!setSchedulingType || schedulingTypeTouched) return;
    applySchedulingType(checked ? 'exact' : defaultSchedulingType);
  };

  const pickupTooEarly =
    schedulingType === 'exact' &&
    !pickupFitsWorkWindow({
      arrivalAtStr: scheduledAtStr,
      pickupAtStr,
      durationHours,
      bufferMinutes: pickupBufferMinutes,
    });

  const outsideShift = useExactTimeOutsideShiftWarning({
    schedulingType,
    scheduledAtStr,
    durationHours,
    ...(mechanicIds !== undefined ? { mechanicIds } : {}),
  });

  // Drop-off opens on the booked day and may not run past the booking (#4124).
  const dropoffMax = parseLocal(latestDropoffAt({ schedulingType, arrivalAtStr: scheduledAtStr }));
  const dropoffReference = parseLocal(
    suggestedDropoffAt({
      schedulingType,
      arrivalAtStr: scheduledAtStr,
      openTime: selectedDate ? openTimeForDate(selectedDate, businessHours) : null,
    }),
  );

  let pickupHelperText = t('create.requestedPickupAtHint');
  if (schedulingType === 'exact' && durationKnown) {
    pickupHelperText = t('create.pickupEstimateHint', {
      duration: formatDuration(durationHours),
      buffer: pickupBufferMinutes,
    });
  } else if (schedulingType === 'exact') {
    pickupHelperText = t('create.pickupEstimateUnknownHint');
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Customer waits first: it is the question that decides whether an
          exact time is needed. Guidance, not a lock (#2115). */}
      <FormControlLabel
        control={
          <Checkbox
            checked={customerWaits}
            onChange={(e) => {
              handleCustomerWaitsChange(e.target.checked);
            }}
          />
        }
        label={t('create.customerWaits')}
      />

      {/* Calendar-reserved duration: always visible, including 0 (#2336).
          Quote-accept with services replaces this with per-service editors
          and still displays the total there (#2667). A create order that
          already has services shows the derived sum read-only (#4123). */}
      {durationOverride ?? (
        <DurationHoursInput
          value={durationHours}
          {...(!durationFromServices && setEstimatedDurationHours
            ? {
                onChange: (hours: number) => {
                  setEstimatedDurationHours(hours);
                  setIsDirty(true);
                },
              }
            : {})}
          stepMinutes={durationStepMinutes}
          label={t('create.estimatedDuration')}
          helperText={
            durationFromServices
              ? t('detail.plannedHoursDerived')
              : durationKnown
                ? t('create.estimatedDurationHint')
                : t('create.estimatedDurationZeroHint')
          }
          warning={!durationKnown}
        />
      )}

      {/* Exact time vs during-the-day (#1138). Only rendered for callers that opt in. */}
      {setSchedulingType && (
        <ToggleButtonGroup
          value={schedulingType}
          exclusive
          onChange={(_e, value: SchedulingType | null) => {
            if (value) handleSchedulingTypeChange(value);
          }}
          size="small"
          aria-label={t('create.schedulingTypeLabel')}
        >
          {/* "During the day" first — it is what most work is (#1931). */}
          <ToggleButton value="floating">{t('create.schedulingTypeFloating')}</ToggleButton>
          <ToggleButton value="exact">{t('create.schedulingTypeExact')}</ToggleButton>
        </ToggleButtonGroup>
      )}

      {/* Work appointment / queue day. Drop-off below follows it until the user edits it; the caller tracks that (#4124). */}
      <Box {...guideAnchor('wo-create.schedule')}>
        <FormLabel sx={{ display: 'block', mb: 1 }}>
          {t(schedulingType === 'floating' ? 'create.scheduledDay' : 'create.scheduledTime')}
        </FormLabel>
        <AvailabilityTimePicker
          value={scheduledAtStr}
          onChange={(v) => {
            setScheduledAtStr(v);
            setIsDirty(true);
          }}
          mode={schedulingType}
          estimatedDurationHours={durationHours}
          serviceIds={serviceIds}
          {...(mechanicIds !== undefined ? { mechanicIds } : {})}
        />
      </Box>

      {/* Same picker as pickup, anchored to the booking: it opens on the
          booked day and cannot run past the appointment (#4124). Empty is
          allowed — the field is optional. */}
      {setVehicleDropoffAtStr && (
        <DateTimePicker
          label={t('create.vehicleDropoffAt')}
          value={parseLocal(vehicleDropoffAtStr) ?? null}
          onChange={(value) => {
            if (value && Number.isNaN(value.getTime())) return;
            setVehicleDropoffAtStr(value ? format(value, DATETIME_LOCAL) : '');
            setIsDirty(true);
          }}
          {...(dropoffMax ? { maxDateTime: dropoffMax } : {})}
          {...(dropoffReference ? { referenceDate: dropoffReference } : {})}
          minutesStep={15}
          ampm={false}
          slotProps={{
            textField: {
              fullWidth: true,
              helperText: t('create.vehicleDropoffAtHint'),
            },
            field: { clearable: true },
            openPickerIcon: { fontSize: 'small' },
          }}
        />
      )}

      {/* Date/time picker for requested pickup */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <DateTimePicker
          label={t('create.requestedPickupAt')}
          value={pickupAtStr ? parse(pickupAtStr, DATETIME_LOCAL, new Date()) : null}
          onChange={(value) => {
            if (value) {
              setPickupAtStr(format(value, DATETIME_LOCAL));
            }
            setIsDirty(true);
          }}
          minDateTime={scheduledAtStr ? parse(scheduledAtStr, DATETIME_LOCAL, new Date()) : undefined}
          minutesStep={15}
          ampm={false}
          slotProps={{
            textField: {
              fullWidth: true,
              // Keep the field in floating mode so a user-set pickup still
              // works. Floating work has no clock arrival, so the generic hint
              // is shown (#2117). Unknown duration must not look like a
              // buffer-only estimate (#2335).
              helperText: pickupHelperText,
            },
            openPickerIcon: { fontSize: 'small' },
          }}
          sx={{ flex: 1 }}
        />
      </Box>

      {pickupTooEarly && (
        <Alert severity="warning">{t('create.pickupTooEarlyWarning')}</Alert>
      )}

      {!hideOutsideShiftWarning && outsideShift && (
        <Alert severity="warning" data-testid="outside-shift-warning">
          {t('create.outsideShiftWarning')}
        </Alert>
      )}

      {/* Remaining flags — own group so the date pickers above keep their space */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={jointInspectionRequested}
              onChange={(e) => {
                setJointInspectionRequested(e.target.checked);
                setIsDirty(true);
              }}
            />
          }
          label={t('create.jointInspectionRequested')}
        />
        <Tooltip title={t('create.allowMinorMaintenanceDesc')} placement="top" arrow>
          <FormControlLabel
            control={
              <Checkbox
                checked={allowMinorMaintenance}
                onChange={(e) => {
                  setAllowMinorMaintenance(e.target.checked);
                  setIsDirty(true);
                }}
              />
            }
            label={t('create.allowMinorMaintenance')}
          />
        </Tooltip>
      </Box>

      {/* Pre-approved additional work limit (#66) */}
      <TextField
        label={t('preapprovedLimit.label')}
        placeholder={t('preapprovedLimit.placeholder')}
        helperText={t('preapprovedLimit.hint')}
        value={preapprovedLimitInput}
        onChange={(e) => {
          setPreapprovedLimitInput(e.target.value);
          setIsDirty(true);
        }}
        type="number"
        slotProps={{
          htmlInput: {
            min: 0,
            step: 10,
          },
          input: {
            endAdornment: <InputAdornment position="end">€</InputAdornment>,
          },
        }}
        fullWidth
        size="small"
      />
    </Box>
  );
}
